import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";
import { ArrowLeft, AlertTriangle, Shield, CheckCircle, Search, ClipboardList } from "lucide-react";
import "@/reports/print.css";
import { QaToolkitPanel } from "@/components/qa/QaToolkitPanel";

interface ProfileResponse {
  question_key: string;
  label: string;
  question: string;
  answer: string;
}

interface ExposureArea {
  area: string;
  band: "Low" | "Medium" | "High" | null;
  rationale: string;
  follow_up: string;
  unknown_count: number;
}

interface RedFlag {
  question: string;
  question_text: string;
  answer: string;
  impact: string;
  changed_recommendation: boolean;
}

interface OverridePlain {
  rule_label: string;
  description: string;
  triggers: { question: string; answer: string }[];
}

interface FollowUpItem {
  area: string;
  question: string;
  response: string;
  why_it_matters: string;
  recommended_follow_up: string;
}

interface TriageSummary {
  questionnaire_id: string;
  assessment_id: string;
  status: "complete" | "incomplete";
  overall_band: "Low" | "Medium" | "High" | null;
  raw_band: "Low" | "Medium" | "High" | null;
  next_step: string | null;
  unknown_pct: number;
  exposure_answered: number;
  exposure_total: number;
  missing_required: number;
  subsector_label: string | null;
  subsector_cohort: "service_based" | "infra_owner" | "mixed_unknown" | null;
  decision_rationale: string[];
  profile_responses: ProfileResponse[];
  exposure_areas: ExposureArea[];
  red_flags: RedFlag[];
  overrides_plain: OverridePlain[];
  follow_up_items: FollowUpItem[];
}

function buildSubsectorParagraph(
  subsector: string | null,
  cohort: TriageSummary["subsector_cohort"],
  band: string | null,
): string | null {
  if (!subsector) return null;
  const label = subsector;
  if (cohort === "service_based") {
    const next =
      band === "High"
        ? "Diligence should focus heavily on the host operator and managed-service relationships, since most of the inherent attack surface sits outside this company's direct control."
        : band === "Medium"
        ? "A targeted review should validate dependency contracts, third-party access pathways, and the host operator's security posture, since those drive most of this company's inherent risk."
        : "Standard contractual safeguards over the host operator and key service providers are sufficient at this stage.";
    return `As a ${label}, this company's inherent cyber exposure is driven less by infrastructure it owns and more by its dependency on host operators, upstream providers, and managed services. Operational responsibility for the underlying telecom surface largely sits with parties outside the company itself, so dependency risk and third-party access weigh heavily in this triage. ${next}`;
  }
  if (cohort === "infra_owner") {
    const next =
      band === "High"
        ? "Full due diligence should focus on the scale and recoverability of the network and operational footprint the company directly owns and operates."
        : band === "Medium"
        ? "A targeted review should validate the security posture of owned network and operational systems, with particular attention to recovery and single points of failure."
        : "Continue periodic reassessment of owned infrastructure and operational resilience as the network footprint evolves.";
    return `As a ${label}, this company directly owns and operates a meaningful share of its telecom surface, so operational responsibility for that surface — and the consequences of any compromise or outage — sit with the company itself. Network footprint, operational complexity, and recovery capability weigh heavily in this triage. ${next}`;
  }
  return `Sub-sector context: ${label}. Risk exposure, dependency risk, and operational responsibility have been interpreted in light of this profile.`;
}

const BAND_BADGE: Record<string, string> = {
  Low: "bg-green-100 text-green-800 border-green-300",
  Medium: "bg-yellow-100 text-yellow-800 border-yellow-300",
  High: "bg-red-100 text-red-800 border-red-300",
};

const BAND_DOT: Record<string, string> = { Low: "🟢", Medium: "🟡", High: "🔴" };

function buildDecisionStatement(band: string | null, summary: TriageSummary): string {
  const areas = (summary.exposure_areas ?? []).filter((a) => a.band === "Medium" || a.band === "High");
  const topAreas = areas
    .sort((a, b) => (b.band === "High" ? 1 : 0) - (a.band === "High" ? 1 : 0))
    .slice(0, 4)
    .map((a) => a.area);
  const hasRedFlag = (summary.red_flags ?? []).some((rf) => rf.changed_recommendation);
  const hasUnknowns = (summary.unknown_pct ?? 0) > 0;

  if (band === "High") {
    let s = "Full cybersecurity due diligence is recommended because the company shows significant inherent exposure across investment-relevant risk areas";
    if (topAreas.length) s += `, including ${topAreas.join(", ")}`;
    s += ".";
    if (hasRedFlag) s += " One or more red-flag conditions independently elevated this recommendation to High.";
    if (hasUnknowns) s += " Several responses indicate uncertainty that should be clarified before close.";
    return s;
  }
  if (band === "Medium") {
    let s = "A targeted cybersecurity review is recommended because the company shows medium exposure across several investment-relevant risk areas";
    if (topAreas.length) s += `, including ${topAreas.join(", ")}`;
    s += ".";
    if (hasRedFlag) s += " One or more red-flag conditions also influenced this recommendation.";
    if (hasUnknowns) s += " Some responses indicate uncertainty that should be clarified before close.";
    return s;
  }
  if (band === "Low") {
    return "No deeper cybersecurity assessment is required at this stage. Inherent exposure is limited relative to typical telecom investments. Maintain standard contractual safeguards and periodic reassessment.";
  }
  return "Insufficient responses to determine a triage outcome.";
}

export default function TriageReportPdf() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { role: effectiveRole } = useEffectiveRole();
  const backPath = effectiveRole === "portfolio_manager" ? "/pm" : "/dashboard";
  const backLabel =
    effectiveRole === "portfolio_manager" ? "Back to Portfolio Dashboard" : "Back to Dashboard";

  const [summary, setSummary] = useState<TriageSummary | null>(null);
  const [orgName, setOrgName] = useState("Organization");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [summaryRes, assessRes] = await Promise.all([
        (supabase.rpc as any)("respondent_get_triage_summary", { p_assessment_id: assessmentId }),
        (supabase.rpc as any)("respondent_get_assessment", { p_assessment_id: assessmentId }),
      ]);
      if (cancelled) return;
      if (!summaryRes.error && summaryRes.data) setSummary(summaryRes.data as TriageSummary);
      else if (summaryRes.error) console.warn("[Triage PDF] summary error:", summaryRes.error);
      if (!assessRes.error && assessRes.data?.length > 0) {
        setOrgName(assessRes.data[0].org_name ?? "Organization");
      }
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.email) setUserName(session.user.email);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [assessmentId]);

  const todayStr = useMemo(
    () => new Date().toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }),
    []
  );

  if (loading) {
    return (
      <div className="p-6 print-container">
        <h2 className="text-xl font-semibold">Preparing Telecom Pre-Investment Cyber Risk Triage Report…</h2>
      </div>
    );
  }

  if (!summary || !summary.overall_band) {
    return (
      <div className="p-6 max-w-lg mx-auto mt-20 text-center space-y-4">
        {assessmentId && (
          <div className="no-print text-left">
            <QaToolkitPanel assessmentId={assessmentId} />
          </div>
        )}
        <h2 className="text-xl font-semibold">No data to display</h2>
        <p className="text-muted-foreground">
          Answer at least the exposure questions in the triage, then return to view the report.
        </p>
        <Button variant="outline" onClick={() => navigate(backPath)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {backLabel}
        </Button>
      </div>
    );
  }

  const band = summary.overall_band ?? "Low";
  const decisionStatement = buildDecisionStatement(band, summary);
  const exposureAreas = summary.exposure_areas ?? [];
  const profileResponses = summary.profile_responses ?? [];
  const redFlags = summary.red_flags ?? [];
  const overrides = summary.overrides_plain ?? [];
  const followUps = summary.follow_up_items ?? [];

  return (
    <div className="print-root">
      <div className="p-6 space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 no-print">
          <Button variant="ghost" size="sm" onClick={() => navigate(backPath)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            {backLabel}
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            Print / Save as PDF
          </Button>
        </div>

        {assessmentId && (
          <div className="no-print">
            <QaToolkitPanel assessmentId={assessmentId} />
          </div>
        )}

        <div className="border-b pb-4">
          <h1 className="text-2xl font-bold">Telecom Pre-Investment Cyber Risk Triage Report</h1>
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mt-1">
            <span>{orgName}</span>
            {userName && <span>• {userName}</span>}
            <span>• {todayStr}</span>
          </div>
        </div>

        {summary.status === "incomplete" && (
          <div className="border border-yellow-300 rounded-lg p-3 bg-yellow-50 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-yellow-700" />
            <p className="text-sm text-yellow-900">
              Triage incomplete — {summary.missing_required} required item(s) unanswered. Results may not reflect the
              full inherent exposure profile.
            </p>
          </div>
        )}

        {/* 1. Executive Triage Decision */}
        <Card className="border-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Executive Triage Decision
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-6 flex-wrap">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Overall Risk Band</p>
                <Badge className={`text-lg px-3 py-1 border ${BAND_BADGE[band]}`}>
                  {BAND_DOT[band]} {band}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Recommended Next Step</p>
                <p className="text-base font-semibold">{summary.next_step}</p>
              </div>
            </div>
            <p className="text-sm leading-relaxed">{decisionStatement}</p>
            {(() => {
              const para = buildSubsectorParagraph(
                summary.subsector_label,
                summary.subsector_cohort,
                band,
              );
              return para ? (
                <p className="text-sm leading-relaxed text-slate-700 border-l-2 border-slate-300 pl-3">
                  {para}
                </p>
              ) : null;
            })()}
          </CardContent>
        </Card>

        {/* 2. Company / Investment Context */}
        {profileResponses.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Company / Investment Context</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                {profileResponses.map((p) => (
                  <div key={p.question_key} className="border-b border-slate-100 pb-2">
                    <dt className="text-xs uppercase tracking-wide text-muted-foreground">{p.label}</dt>
                    <dd className="text-sm font-medium text-slate-900 mt-0.5">{p.answer}</dd>
                  </div>
                ))}
              </dl>
            </CardContent>
          </Card>
        )}

        {/* 3. Why This Decision Was Reached */}
        {summary.decision_rationale && summary.decision_rationale.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Why This Decision Was Reached</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed">
                {summary.decision_rationale.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* 4. Exposure Areas */}
        {exposureAreas.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Exposure Areas</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {exposureAreas.map((a) => {
                  const b = a.band ?? "Low";
                  return (
                    <div key={a.area} className="border rounded-lg p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-medium text-sm">{a.area}</p>
                          <p className="text-sm text-slate-600 mt-1">{a.rationale}</p>
                          <p className="text-sm text-slate-700 mt-2">
                            <span className="font-medium">Suggested follow-up: </span>
                            {a.follow_up}
                          </p>
                        </div>
                        <div className="shrink-0">
                          {a.band ? (
                            <Badge className={`border ${BAND_BADGE[b]}`}>
                              {BAND_DOT[b]} {a.band}
                            </Badge>
                          ) : (
                            <Badge variant="outline">No data</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 5. Red Flags */}
        {redFlags.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
                Red Flags
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                {redFlags.map((rf, i) => (
                  <div key={i} className="border-l-4 border-yellow-400 pl-3">
                    <p className="font-medium">{rf.question}</p>
                    <p className="text-slate-600 mt-0.5">{rf.question_text}</p>
                    <p className="mt-1">
                      <span className="text-muted-foreground">Response: </span>
                      <span className="font-medium">{rf.answer}</span>
                    </p>
                    <p className="text-slate-700 mt-1">{rf.impact}</p>
                    {rf.changed_recommendation && (
                      <p className="text-xs text-yellow-800 mt-1 italic">This response influenced the recommendation.</p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Risk overrides applied (plain English) */}
        {overrides.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Search className="h-5 w-5" />
                Risk Overrides Applied
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 text-sm">
                {overrides.map((o, i) => (
                  <div key={i} className="border rounded-lg p-3">
                    <p className="font-medium">{o.rule_label}</p>
                    <p className="text-slate-700 mt-1">{o.description}</p>
                    {o.triggers && o.triggers.length > 0 && (
                      <ul className="mt-2 text-xs text-slate-600 list-disc pl-5">
                        {o.triggers.map((t, j) => (
                          <li key={j}>
                            <span className="font-medium">{t.question}</span> — answered "{t.answer}"
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* 6. Items Requiring Follow-Up */}
        {followUps.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ClipboardList className="h-5 w-5" />
                Items Requiring Follow-Up
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="py-2 pr-3 font-medium text-muted-foreground w-1/6">Area</th>
                      <th className="py-2 pr-3 font-medium text-muted-foreground w-2/6">Question</th>
                      <th className="py-2 pr-3 font-medium text-muted-foreground w-1/12">Response</th>
                      <th className="py-2 pr-3 font-medium text-muted-foreground w-2/6">Why It Matters</th>
                      <th className="py-2 font-medium text-muted-foreground w-2/6">Recommended Follow-Up</th>
                    </tr>
                  </thead>
                  <tbody>
                    {followUps.map((f, i) => (
                      <tr key={i} className="border-b last:border-b-0 align-top">
                        <td className="py-2 pr-3 font-medium">{f.area}</td>
                        <td className="py-2 pr-3 text-slate-700">{f.question}</td>
                        <td className="py-2 pr-3">
                          <Badge variant="outline">{f.response ?? "—"}</Badge>
                        </td>
                        <td className="py-2 pr-3 text-slate-700">{f.why_it_matters}</td>
                        <td className="py-2 text-slate-700">{f.recommended_follow_up}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                These items were answered "Unknown" or left incomplete and should be clarified during follow-up
                diligence.
              </p>
            </CardContent>
          </Card>
        )}

        {/* 7. Recommended Next Steps */}
        <Card
          className={`border-2 ${
            band === "High" ? "border-red-300" : band === "Medium" ? "border-yellow-300" : "border-green-300"
          }`}
        >
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              {band === "Low" ? <CheckCircle className="h-5 w-5" /> : <Search className="h-5 w-5" />}
              Recommended Next Steps
            </CardTitle>
          </CardHeader>
          <CardContent>
            {band === "High" && (
              <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed">
                <li>Engage a full cybersecurity due diligence covering governance, identity & access, third parties, infrastructure, and incident readiness.</li>
                <li>Request privileged access policies, admin account inventory, and MFA evidence.</li>
                <li>Review incident history and any open or recent regulatory enforcement actions.</li>
                <li>Validate concentration on critical vendors and any flagged-vendor exposure in core systems.</li>
                <li>Confirm cross-border data flows and the lawful intercept / regulated-data handling posture.</li>
                <li>Clarify all items in the follow-up table above before close.</li>
              </ul>
            )}
            {band === "Medium" && (
              <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed">
                <li>Run a targeted cybersecurity review focused on the highest-exposure areas listed above.</li>
                <li>Request documentary evidence (policies, vendor list, incident history) for the medium and high areas.</li>
                <li>Validate privileged access controls and third-party administrative access posture.</li>
                <li>Resolve all "Unknown" responses in the follow-up table.</li>
                <li>Capture remediation actions and conditions in the deal documentation.</li>
              </ul>
            )}
            {band === "Low" && (
              <ul className="list-disc pl-5 space-y-2 text-sm leading-relaxed">
                <li>No deeper cybersecurity assessment required at this stage.</li>
                <li>Maintain standard contractual safeguards (security and privacy reps & warranties).</li>
                <li>Schedule periodic reassessment post-close (annual or material-change driven).</li>
                <li>Resolve any minor "Unknown" follow-ups during routine onboarding.</li>
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
